import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import LoginModal from './LoginModal';
import PostRequirementForm from './PostRequirementForm';
import MegaMenu from './MegaMenu';
import HeaderUtilityBar from './header/HeaderUtilityBar';
import HeaderSearch from './header/HeaderSearch';
import HeaderActions from './header/HeaderActions';
import HeaderMobileDrawer from './header/HeaderMobileDrawer';
import { useSession } from './header/useSession';
import { useData } from '../context/DataContext';
import { API } from '../lib/api';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../context/I18nContext';

const Header = () => {
  const navigate = useNavigate();
  const { trending, branding = {}, cities = [], footerCfg = {} } = useData();
  const { theme, toggle: toggleTheme } = useTheme();
  const { lang, setLang, t } = useI18n();
  const [loginOpen, setLoginOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [session, setSession] = useSession();

  const logoutSession = async () => {
    if (!session) return;
    // Best-effort server-side cookie clear; ignore errors (we always clear local state).
    const path = session.role === 'vendor' ? '/vendor/auth/logout' : '/auth/logout';
    try { await axios.post(`${API}${path}`); } catch (err) {
      console.warn('[Header] logout API failed; clearing local state anyway:', err?.message || err);
    }
    if (session.role === 'vendor') {
      localStorage.removeItem('tgmart_vendor_token');
      localStorage.removeItem('tgmart_vendor');
    } else {
      localStorage.removeItem('tgmart_token');
      localStorage.removeItem('tgmart_user');
    }
    window.dispatchEvent(new Event('tgmart-session-change'));
    setSession(null);
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-40 bg-white dark:bg-[#0b1220] shadow-sm isolate transition-colors">
      <HeaderUtilityBar
        footerCfg={footerCfg}
        lang={lang}
        setLang={setLang}
        theme={theme}
        toggleTheme={toggleTheme}
        session={session}
      />

      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-2 lg:py-0 lg:h-16 flex flex-wrap lg:flex-nowrap items-center gap-y-2 gap-x-3">
          <Link to="/" className="flex items-center gap-1 shrink-0 order-1 lg:order-1" data-testid="site-logo-link">
            {branding.logo_url ? (
              <img
                src={branding.logo_url.startsWith('http') ? branding.logo_url : `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`}
                alt={branding.site_name || 'TelanganaMart'}
                className="h-8 sm:h-10 w-auto object-contain"
                data-testid="site-logo-img"
              />
            ) : (
              <div className="flex items-center" data-testid="site-logo-text">
                <span className="text-[#ee5e23] font-extrabold text-2xl sm:text-3xl tracking-tight">Telangana</span>
                <span className="text-[#2c54b2] font-extrabold text-2xl sm:text-3xl tracking-tight">Mart</span>
              </div>
            )}
          </Link>

          <HeaderSearch
            trending={trending}
            cities={cities}
            placeholder={t('search.placeholder')}
            searchLabel={t('search.button')}
          />

          <HeaderActions
            session={session}
            onLogout={logoutSession}
            onPostRequirement={() => setPostOpen(true)}
            t={t}
          />

          <HeaderMobileDrawer
            open={mobileMenuOpen}
            onOpenChange={setMobileMenuOpen}
            session={session}
            onLogout={logoutSession}
            onPostRequirement={() => setPostOpen(true)}
            theme={theme}
            toggleTheme={toggleTheme}
            lang={lang}
            setLang={setLang}
            t={t}
          />
        </div>
      </div>

      <MegaMenu />

      <LoginModal open={loginOpen} onOpenChange={setLoginOpen} />
      <PostRequirementForm open={postOpen} onOpenChange={setPostOpen} />
    </header>
  );
};

export default Header;
